/**
 * ChatModalsLayer — the chat-level modal cluster lifted out of ChatBox's JSX
 * (delegation pattern). The JSX subtrees + every handler body are reproduced
 * VERBATIM from ChatBox: OAuthConsentModal, UsageLimitModal (incl. the
 * analytics console.debug + window CustomEvent dispatches),
 * ContactCardPopover, NoteCardPopover. Imports stay within the chat dispatcher
 * pattern (features/common plus downward modules/contacts for the contact
 * popover). The orchestrator keeps every piece of state and
 * threads it as props, so render timing / analytics side effects are unchanged.
 */
import { useEffect, useState } from 'react';
import { OAuthConsentModal, type OAuthConsentProvider } from '@/features/oauth/components/OAuthConsentModal';
import { UsageLimitModal, type UsageLimitMode } from '@/common/components/UsageLimitModal';
import { ContactCardPopover } from '@/modules/contacts/components/ContactCardPopover';
import { NoteCardPopover } from '@/features/chat/components/NoteCardPopover';
import DocEditorModal from '@/modules/drive/docs/DocEditorModal';
import type { DocForEditor } from '@/modules/drive/docs/DocEditorModal';
import { http } from '@/lib/http';
import { useSession } from '@/features/auth/hooks/useSession';
import type { NavigateFunction } from 'react-router-dom';
import type { ChatUIAction } from '@/lib/api/endpoints/chat';

export interface ChatModalsLayerProps {
  // OAuth consent interstitial
  pendingOAuthConsent: { provider: OAuthConsentProvider; resolve: (ok: boolean) => void } | null;
  setPendingOAuthConsent: (value: { provider: OAuthConsentProvider; resolve: (ok: boolean) => void } | null) => void;
  // Usage / storage limit modal
  storageExceeded: boolean;
  setStorageExceeded: (value: boolean) => void;
  limitState: {
    open: boolean;
    mode: UsageLimitMode;
    resetsAt?: string;
    retryAfterSec?: number;
    errorCode?: string;
  };
  setLimitDismissed: (value: boolean) => void;
  navigate: NavigateFunction;
  // Object/note card popovers
  objectCardPopover: { objectType: string; objectId: string } | null;
  setObjectCardPopover: (value: { objectType: string; objectId: string } | null) => void;
  noteModal: { noteId: string } | null;
  setNoteModal: (value: { noteId: string } | null) => void;
  docModal: { docId: string } | null;
  setDocModal: (value: { docId: string } | null) => void;
  applyUiActions: (actions: ChatUIAction[]) => void;
  activeConversationId?: string;
}

export function ChatModalsLayer({
  pendingOAuthConsent,
  setPendingOAuthConsent,
  storageExceeded,
  setStorageExceeded,
  limitState,
  setLimitDismissed,
  navigate,
  objectCardPopover,
  setObjectCardPopover,
  noteModal,
  setNoteModal,
  docModal,
  setDocModal,
  applyUiActions,
  activeConversationId,
}: ChatModalsLayerProps) {
  const { token } = useSession();
  const [docForEditor, setDocForEditor] = useState<DocForEditor | null>(null);

  useEffect(() => {
    if (!docModal) { setDocForEditor(null); return; }
    void (async () => {
      try {
        const res = await http.get<any>(`/notes/${docModal.docId}`, { authToken: token ?? undefined });
        if (res.data) {
          setDocForEditor({
            id: res.data.id,
            title: res.data.title || 'Untitled',
            body: res.data.body || res.data.content || '',
            _etag: res.etag || res.data._etag,
            authorId: res.data.authorId,
            checkpoints: res.data.checkpoints,
          });
        } else {
          setDocModal(null);
        }
      } catch {
        setDocModal(null);
      }
    })();
  }, [docModal, token, setDocModal]);
  return (
    <>
      <OAuthConsentModal
        open={pendingOAuthConsent !== null}
        provider={pendingOAuthConsent?.provider ?? null}
        onConfirm={() => {
          pendingOAuthConsent?.resolve(true);
          setPendingOAuthConsent(null);
        }}
        onCancel={() => {
          pendingOAuthConsent?.resolve(false);
          setPendingOAuthConsent(null);
        }}
      />

      <UsageLimitModal
        open={storageExceeded || limitState.open}
        mode={storageExceeded ? 'storage_exceeded' : limitState.mode}
        resetsAt={storageExceeded ? undefined : limitState.resetsAt}
        retryAfterSec={storageExceeded ? undefined : limitState.retryAfterSec}
        onUpgrade={() => {
          if (storageExceeded) {
            console.debug('[usage-limit-modal] upgrade_clicked', { errorCode: 'STORAGE_LIMIT_EXCEEDED' });
            window.dispatchEvent(new CustomEvent('xavior:usage-limit-modal', {
              detail: { action: 'upgrade_clicked', errorCode: 'STORAGE_LIMIT_EXCEEDED' },
            }));
          } else {
            console.debug('[usage-limit-modal] upgrade_clicked', { errorCode: limitState.errorCode });
            window.dispatchEvent(new CustomEvent('xavior:usage-limit-modal', {
              detail: { action: 'upgrade_clicked', errorCode: limitState.errorCode },
            }));
          }
          navigate({ search: '?upgrade=1' }, { replace: true });
        }}
        onDismiss={() => {
          if (storageExceeded) {
            console.debug('[usage-limit-modal] dismissed', { errorCode: 'STORAGE_LIMIT_EXCEEDED' });
            window.dispatchEvent(new CustomEvent('xavior:usage-limit-modal', {
              detail: { action: 'dismissed', errorCode: 'STORAGE_LIMIT_EXCEEDED' },
            }));
            setStorageExceeded(false);
          } else {
            console.debug('[usage-limit-modal] dismissed', { errorCode: limitState.errorCode });
            window.dispatchEvent(new CustomEvent('xavior:usage-limit-modal', {
              detail: { action: 'dismissed', errorCode: limitState.errorCode },
            }));
            setLimitDismissed(true);
          }
        }}
      />

      {objectCardPopover && (
        <ContactCardPopover
          objectId={objectCardPopover.objectId}
          objectType={objectCardPopover.objectType}
          onClose={() => setObjectCardPopover(null)}
        />
      )}
      {docModal && docForEditor && (
        <DocEditorModal
          doc={docForEditor}
          onClose={() => { setDocModal(null); setDocForEditor(null); }}
        />
      )}
      {noteModal && (
        <NoteCardPopover
          noteId={noteModal.noteId}
          onClose={() => setNoteModal(null)}
          onOpenInModule={(id) => {
            applyUiActions([
              {
                type: 'module.focus',
                moduleType: 'notes',
                objectType: 'note',
                objectId: id,
                conversationId: activeConversationId,
              } as ChatUIAction,
            ]);
            setNoteModal(null);
          }}
        />
      )}
    </>
  );
}
